import { MotionPlayIcon } from '@/components/icons'
import { SortableTableHead } from '@/components/projects/SortableTableHead'
import { WorkItemStatusBadge } from '@/components/projects/WorkItemStatusBadge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Intent } from '@/types'
import { INTENT_DEFAULT_SORT_DIRECTIONS, type IntentSortColumn } from '@/types/intent'
import { Check, Minus, Trash2 } from 'lucide-react'
import type { IntentColumn } from './IntentsFilters'

function formatIntentNumber(num: number | null): string {
  if (num === null) return '—'
  return `# ${String(num).padStart(3, '0')}`
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString()
}

const intentCheckboxClass =
  'h-6 w-6 shrink-0 border-2 border-primary rounded bg-transparent data-[state=checked]:border-0 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:border-0 data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground'

interface IntentsTableProps {
  intents: Intent[]
  showCheckboxes: boolean
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onToggleSelectAll: () => void
  visibleColumns: IntentColumn[]
  canDelete: boolean
  canTrigger: boolean
  sortBy: IntentSortColumn
  sortDirection: 'asc' | 'desc'
  onSortChange: (column: IntentSortColumn) => void
  onSelect: (intent: Intent) => void
  onDelete: (intent: Intent) => void
  onTrigger: (intent: Intent) => void
}

export function IntentsTable({
  intents,
  showCheckboxes,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  visibleColumns,
  canDelete,
  canTrigger,
  sortBy,
  sortDirection,
  onSortChange,
  onSelect,
  onDelete,
  onTrigger,
}: IntentsTableProps) {
  const allSelected = intents.length > 0 && intents.every(d => selectedIds.has(d.id))
  const someSelected = !allSelected && intents.some(d => selectedIds.has(d.id))
  const showActions = canDelete || canTrigger

  return (
    <Table aria-label="Intents">
      <TableHeader>
        <TableRow className="bg-muted/30">
          {showCheckboxes && (
            <TableHead className="w-[56px]">
              <Checkbox
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={onToggleSelectAll}
                aria-label="Select all intents"
                className={intentCheckboxClass}
              >
                {allSelected ? <Check className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
              </Checkbox>
            </TableHead>
          )}
          {visibleColumns.includes('number') && (
            <SortableTableHead
              column="number"
              label="#"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={INTENT_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
              className="w-[80px]"
            />
          )}
          {visibleColumns.includes('name') && (
            <SortableTableHead
              column="name"
              label="Name"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={INTENT_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
            />
          )}
          {visibleColumns.includes('epic') && (
            <SortableTableHead
              column="epic"
              label="Epic"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={INTENT_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
              className="w-[120px]"
            />
          )}
          {visibleColumns.includes('feature') && (
            <SortableTableHead
              column="feature"
              label="Feature"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={INTENT_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
              className="w-[120px]"
            />
          )}
          {visibleColumns.includes('status') && (
            <SortableTableHead
              column="status"
              label="Status"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={INTENT_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
              className="w-[170px]"
            />
          )}
          {visibleColumns.includes('created') && (
            <SortableTableHead
              column="created"
              label="Created"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={INTENT_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
              className="w-[120px]"
            />
          )}
          {showActions && <TableHead className="w-[200px]">Actions</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {intents.map((intent) => (
          <TableRow
            key={intent.id}
            className="cursor-pointer"
            data-testid={`intent-row-${intent.id}`}
            onClick={() => onSelect(intent)}
          >
            {showCheckboxes && (
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selectedIds.has(intent.id)}
                  onCheckedChange={() => onToggleSelect(intent.id)}
                  aria-label={`Select intent ${intent.name}`}
                  className={intentCheckboxClass}
                />
              </TableCell>
            )}
            {visibleColumns.includes('number') && (
              <TableCell className="font-medium text-muted-foreground">
                {formatIntentNumber(intent.number)}
              </TableCell>
            )}
            {visibleColumns.includes('name') && (
              <TableCell>
                <span className="font-medium">{intent.name}</span>
              </TableCell>
            )}
            {visibleColumns.includes('epic') && (
              <TableCell>{intent.epic ?? ''}</TableCell>
            )}
            {visibleColumns.includes('feature') && (
              <TableCell>{intent.feature ?? ''}</TableCell>
            )}
            {visibleColumns.includes('status') && (
              <TableCell>
                <WorkItemStatusBadge status={intent.status} />
              </TableCell>
            )}
            {visibleColumns.includes('created') && (
              <TableCell className="text-muted-foreground">
                {formatDate(intent.createdAt)}
              </TableCell>
            )}
            {showActions && (
              <TableCell onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2">
                  {canTrigger && (
                    <button
                      type="button"
                      className="flex items-center gap-2 px-3 py-2 text-sm font-bold text-foreground hover:bg-muted rounded-md"
                      onClick={() => onTrigger(intent)}
                      aria-label={`Trigger workflow for intent ${formatIntentNumber(intent.number)}`}
                    >
                      <MotionPlayIcon className="h-5 w-5 shrink-0 text-action-icon" />
                      Trigger
                    </button>
                  )}
                  {canDelete && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onDelete(intent)}
                      aria-label={`Delete intent ${intent.name}`}
                    >
                      <Trash2 className="h-4 w-4 text-action-icon" />
                    </Button>
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
